import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import api from "../../services/api";
import Cookies from "js-cookie";
import MapWithSearch from "../../components/MapWithSearch";

interface SignupResponse {
  success: boolean;
  token: string;
  error?: string;
  data: {
    id: string;
    email: string;
    role: string;
  };
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Starting opening hours (same defaults as the Locations page); owners can
// change them later in Locations.
const defaultTimelines = () => ({
  storeTimeline: DAYS.map((day) => ({
    day,
    isOpen: day !== "Sunday",
    slots: day !== "Sunday" ? [{ start: "09:00", end: "21:00" }] : [],
  })),
  pickupTimeline: DAYS.map((day) => ({
    day,
    isOpen: true,
    slots: [
      { start: "08:00", end: "14:00" },
      { start: "17:00", end: "23:00" },
    ],
  })),
  deliveryTimeline: DAYS.map((day) => ({
    day,
    isOpen: true,
    slots: [{ start: "10:00", end: "22:00" }],
  })),
});

const inputClass =
  "w-full p-3 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelClass = "block mb-2 text-gray-700 text-sm font-medium";

const numberOrNull = (value: string) => (value === "" ? null : Number(value));

const Toggle: React.FC<{
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, hint, checked, onChange }) => (
  <label className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
    <span>
      <span className="block text-sm font-semibold text-gray-800">{label}</span>
      <span className="block text-xs text-gray-500">{hint}</span>
    </span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-5 w-5 accent-blue-600"
    />
  </label>
);

/**
 * One-step signup: the admin account (auto-approved, 15-day trial) and the
 * restaurant's first location are created together.
 */
const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Restaurant (first location)
  const [hotelName, setHotelName] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [defaultTip, setDefaultTip] = useState(true);
  const [cashEnabled, setCashEnabled] = useState(true);
  const [deliveryTipToPartner, setDeliveryTipToPartner] = useState(false);
  const [baseTipPercent, setBaseTipPercent] = useState("");
  const [tipGapPercent, setTipGapPercent] = useState("");
  const [withinRadius, setWithinRadius] = useState("");
  const [minOrderAmount, setMinOrderAmount] = useState("");
  const [deliveryAmount, setDeliveryAmount] = useState("");
  const [asapDelay, setAsapDelay] = useState("");

  const handleLocationChange = (data: any) => {
    if (data.fullAddress || data.address) setAddress(data.fullAddress || data.address);
    if (data.lat) setLatitude(data.lat);
    if (data.lng) setLongitude(data.lng);
    if (data.city) setCity(data.city);
    if (data.state) setState(data.state);
    if (data.country) setCountry(data.country);
    // Prefill name/phone from the map place, if the owner hasn't typed them.
    if (data.placeName && !hotelName) setHotelName(data.placeName);
    if (data.phoneNumber && !phoneNo) setPhoneNo(String(data.phoneNumber));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!hotelName.trim() || !phoneNo.trim()) {
      toast.error("Please enter your restaurant's name and phone number");
      return;
    }
    if (!latitude || !longitude) {
      toast.error("Please select your restaurant's location on the map");
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.post<SignupResponse>("/admin/register", {
        email,
        password,
        location: {
          hotelName: hotelName.trim(),
          phoneNo: phoneNo.replace(/\D/g, ""),
          address,
          latitude,
          longitude,
          city,
          state,
          country,
          defaultTip,
          cashEnabled,
          deliveryTipToPartner,
          baseTipPercent: numberOrNull(baseTipPercent),
          tipGapPercent: numberOrNull(tipGapPercent),
          withinRadius: numberOrNull(withinRadius),
          minOrderAmount: numberOrNull(minOrderAmount),
          deliveryAmount: numberOrNull(deliveryAmount),
          asapDelay: asapDelay === "" ? 0 : Number(asapDelay),
          ...defaultTimelines(),
        },
      });

      if (data.success) {
        Cookies.set("token", data.token);
        toast.success("Welcome! Your restaurant is ready and your 15-day free trial has started.");
        navigate("/admin");
      } else {
        toast.error(data.error || "Signup failed");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-10">
      <div className="mx-auto w-full max-w-2xl rounded-lg bg-white p-6 shadow-md sm:p-8">
        <h1 className="mb-2 text-center text-2xl font-semibold text-blue-600">
          Create Your Account
        </h1>
        <p className="mb-8 text-center text-sm text-gray-500">
          Set up your account and restaurant in one go. 15-day free trial — no credit card required.
        </p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Account */}
          <section className="space-y-4">
            <h2 className="m-0 text-lg font-semibold text-gray-800">Your account</h2>
            <div>
              <label htmlFor="email" className={labelClass}>Email *</label>
              <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputClass} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="password" className={labelClass}>Password *</label>
                <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className={inputClass} />
              </div>
              <div>
                <label htmlFor="confirmPassword" className={labelClass}>Confirm Password *</label>
                <input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className={inputClass} />
              </div>
            </div>
          </section>

          {/* Restaurant */}
          <section className="space-y-4">
            <h2 className="m-0 text-lg font-semibold text-gray-800">Your restaurant</h2>
            <div>
              <label className={labelClass}>Location *</label>
              <MapWithSearch onLocationChange={handleLocationChange} />
              {address && <p className="mt-2 text-xs text-gray-500">Selected: {address}</p>}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Restaurant / Location Name *</label>
                <input value={hotelName} onChange={(e) => setHotelName(e.target.value)} required className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Phone Number *</label>
                <input type="tel" value={phoneNo} onChange={(e) => setPhoneNo(e.target.value)} required className={inputClass} />
              </div>
            </div>
            <Toggle label="Show default tip selected" hint="Prompt customers for tips during checkout." checked={defaultTip} onChange={setDefaultTip} />
            <Toggle label="Cash Enabled" hint="Allow cash payments at this location." checked={cashEnabled} onChange={setCashEnabled} />
          </section>

          {/* Tips */}
          <section className="space-y-4">
            <h2 className="m-0 text-lg font-semibold text-gray-800">Tip settings</h2>
            <Toggle label="Delivery Tip to Partner" hint="Share delivery tips with delivery partners." checked={deliveryTipToPartner} onChange={setDeliveryTipToPartner} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Base Tip Percent (%)</label>
                <input type="number" min={0} value={baseTipPercent} onChange={(e) => setBaseTipPercent(e.target.value)} placeholder="e.g. 15" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Tip Gap Percent (%)</label>
                <input type="number" min={0} value={tipGapPercent} onChange={(e) => setTipGapPercent(e.target.value)} placeholder="e.g. 5" className={inputClass} />
              </div>
            </div>
          </section>

          {/* Delivery */}
          <section className="space-y-4">
            <h2 className="m-0 text-lg font-semibold text-gray-800">Delivery settings</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className={labelClass}>Within Radius (miles)</label>
                <input type="number" min={0} value={withinRadius} onChange={(e) => setWithinRadius(e.target.value)} placeholder="e.g. 10" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Min Order Amount</label>
                <input type="number" min={0} value={minOrderAmount} onChange={(e) => setMinOrderAmount(e.target.value)} placeholder="e.g. 20" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Delivery Amount</label>
                <input type="number" min={0} value={deliveryAmount} onChange={(e) => setDeliveryAmount(e.target.value)} placeholder="e.g. 5" className={inputClass} />
              </div>
            </div>
            <div>
              <label className={labelClass}>ASAP Order Delay (minutes)</label>
              <input type="number" min={0} value={asapDelay} onChange={(e) => setAsapDelay(e.target.value)} placeholder="e.g. 15" className={inputClass} />
            </div>
            <p className="m-0 text-xs text-gray-500">
              Opening hours start as Mon–Sat 9 AM–9 PM; change them anytime in Locations.
            </p>
          </section>

          <button
            type="submit"
            disabled={loading}
            className={`w-full rounded-md p-3 text-lg font-medium text-white transition-all duration-150 ${
              loading
                ? "cursor-not-allowed bg-blue-600 opacity-70"
                : "bg-blue-600 hover:bg-[#0c2b4b] hover:shadow-[0_0_12px_rgba(0,123,255,0.3)]"
            }`}
          >
            {loading ? "Setting up your restaurant..." : "Start Free Trial"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{" "}
          <Link to="/admin/login" className="text-blue-600 underline">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
